import { Injectable } from '@angular/core';
import type { MasterEventDTO } from '@app/interface/subject/subject.interface';
import { EventType } from '@app/interface/subject/subject.interface';
import { MS_PER_DAY, toLocalDate } from '@app/utils/dates';

@Injectable({
  providedIn: 'root'
})
export class MasterEventConsolidationService {
  consolidateEvents(masterEvents?: MasterEventDTO[]): MasterEventDTO[] {
    masterEvents ??= [];

    const masterEventsByApoKey = this.groupByApoKey(masterEvents);

    const consolidatedEvents = Object.values(masterEventsByApoKey).flatMap((masterEvents) =>
      this.consolidateApoEvents(masterEvents)
    );

    return this.sortMasterEvents(consolidatedEvents, false);
  }

  private groupByApoKey(masterEvents: MasterEventDTO[]): Record<string, MasterEventDTO[]> {
    return masterEvents.reduce((acc, masterEvent) => {
      const masterEvents = (acc[masterEvent.apoKey] ??= []);

      masterEvents.push(masterEvent);

      return acc;
    }, {});
  }

  private consolidateApoEvents(masterEvents: MasterEventDTO[]): MasterEventDTO[] {
    const reorderEvents = masterEvents
      .filter((e) => Boolean(e.subjectInfo.portraitIdAccessCode))
      .map((reorderEvent) => this.toMasterEvent(reorderEvent, reorderEvent));

    if (reorderEvents.length) {
      return reorderEvents;
    }

    const closestUpcomingEvent = this.getClosestUpcomingEvent(masterEvents);

    if (closestUpcomingEvent) {
      return [closestUpcomingEvent];
    }

    return [];
  }

  private getClosestUpcomingEvent(masterEvents: MasterEventDTO[]): MasterEventDTO | null {
    masterEvents = this.sortMasterEvents(masterEvents, true);

    const upcomingEvents = this.getAllUpcomingEvents(masterEvents);

    if (!upcomingEvents.length) {
      return null;
    }

    const closestUpcomingEvent = upcomingEvents[0];

    return this.toMasterEvent(closestUpcomingEvent, upcomingEvents.at(-1)!);
  }

  private getAllUpcomingEvents(masterEvents: MasterEventDTO[]): MasterEventDTO[] {
    const now = Date.now();

    return masterEvents.filter((e) => e.type === EventType.ORIGINAL).filter((e) => this.getEventEndTime(e) > now);
  }

  private toMasterEvent(masterEvent: MasterEventDTO, lastMasterEvent: MasterEventDTO): MasterEventDTO {
    return {
      ...masterEvent,
      eventType: {
        ...masterEvent.eventType!,
        lastPictureDay: lastMasterEvent.eventType!.lastPictureDay
      }
    };
  }

  /**
   * Exclusive end of the event: midnight following the last picture day, falling back to the event date
   * when the last picture day is missing. Mirrors the server rule (PictureDateHelper.isAfterPictureDate),
   * which keeps a job active for the whole of its last picture day.
   *
   * Sort and filter share this key. Keying the "closest upcoming" selection off the same end time the
   * filter uses keeps them aligned: the surviving event is the active one ending soonest, never a
   * long-running event whose start date is weeks in the past that a sort on eventDate would float to the top.
   */
  private getEventEndTime(masterEvent: MasterEventDTO): number {
    const { eventDate, lastPictureDay } = masterEvent.eventType!;

    return toLocalDate(lastPictureDay || eventDate).getTime() + MS_PER_DAY;
  }

  private sortMasterEvents<T extends MasterEventDTO>(masterEvents: T[], asc: boolean): T[] {
    const dir = asc ? 1 : -1;

    return masterEvents.sort((a, b) => (this.getEventEndTime(a) - this.getEventEndTime(b)) * dir);
  }
}
